// Typed auth API. Swappable: when VITE_USE_MOCKS is off it calls the real backend,
// otherwise it uses the in-app mock. Both honor the same contracts in ./types.
// The backend wraps responses as { ok, ... }; we unwrap to the contract shapes here.
import { ApiError, apiClient } from "@/lib/apiClient";
import { env } from "@/lib/env";
import { mockAuthApi } from "@/mocks/mockAuth";
import type {
  AccountStatus,
  AuthResult,
  AuthUser,
  LoginInput,
  MeResult,
  RegisterCompleteInput,
  RegisterStartInput,
  StartResult,
} from "./types";

interface OkAuth {
  ok: true;
  token: string;
  user: AuthUser;
  status?: AccountStatus;
  deviceLimitReached?: boolean;
}

/** True when a login/admin-login was rejected because the device isn't trusted yet
 *  (or an abuse flag): the caller should route into the re-verification flow. */
export function isReverifyError(err: unknown): boolean {
  return (
    err instanceof ApiError &&
    typeof err.body === "object" &&
    err.body !== null &&
    (err.body as { code?: string }).code === "reverify"
  );
}
interface OkStart {
  ok: true;
  sentTo: string | null;
}
interface OkMe {
  ok: true;
  user: AuthUser;
  status: AccountStatus;
  emailVerified: boolean;
}

export interface AuthApi {
  login(input: LoginInput): Promise<AuthResult>;
  adminLogin(input: LoginInput): Promise<AuthResult>;
  adminResetStart(email: string): Promise<StartResult>;
  adminResetVerify(email: string, code: string, deviceToken?: string): Promise<AuthResult>;
  registerStart(input: RegisterStartInput): Promise<StartResult>;
  registerComplete(input: RegisterCompleteInput): Promise<AuthResult>;
  me(): Promise<MeResult>;
}

const realAuthApi: AuthApi = {
  async login(input) {
    const r = await apiClient.post<OkAuth>("/auth/login", input, { anonymous: true });
    return { token: r.token, user: r.user, status: r.status };
  },

  async adminLogin(input) {
    const r = await apiClient.post<OkAuth>("/auth/admin-login", input, { anonymous: true });
    return { token: r.token, user: r.user };
  },

  async adminResetStart(email) {
    const r = await apiClient.post<OkStart>("/auth/admin-reset/start", { email }, { anonymous: true });
    return { sentTo: r.sentTo };
  },

  async adminResetVerify(email, code, deviceToken) {
    const r = await apiClient.post<OkAuth>(
      "/auth/admin-reset/verify",
      { email, code, deviceToken },
      { anonymous: true }
    );
    return { token: r.token, user: r.user, deviceLimitReached: r.deviceLimitReached };
  },

  async registerStart(input) {
    const r = await apiClient.post<OkStart>("/auth/register/start", input, { anonymous: true });
    return { sentTo: r.sentTo };
  },

  async registerComplete(input) {
    const r = await apiClient.post<OkAuth>("/auth/register/complete", input, { anonymous: true });
    return { token: r.token, user: r.user, status: r.status, deviceLimitReached: r.deviceLimitReached };
  },

  async me() {
    const r = await apiClient.get<OkMe>("/auth/me");
    return { user: r.user, status: r.status, emailVerified: r.emailVerified };
  },
};

export const authApi: AuthApi = env.useMocks ? mockAuthApi : realAuthApi;
